import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatWalletBalanceMinor } from '../../services/channelService';
import {
  getInternalWalletB2BPing,
  isInternalWalletApiConfigured,
  postInternalApproveCreditIssueRequest,
  postInternalRejectCreditIssueRequest,
} from '../../services/internalWalletService';
import { listCreditIssueRequests as fetchPublicCreditIssueRequests, listWallets } from '../../services/walletPublicService';
import { getAxiosErrorMessage, getWelcomeGreeting, logoutRemote } from '../../services/authService';
import { INTERNAL_SIGN_IN_URL, getInternalErrorMessage, openInternalSignIn } from '../../services/internalApi';
import { parseInrMajorToMinor } from '../../utils/walletTransfer';
import { useToast } from '../../context/ToastContext';
import {
  amountMinorFromRow,
  createdMsFromRow,
  repaymentDateFromRow,
  repaymentDateMsFromRow,
  requestIdFromRow,
  requestorNoteFromRow,
  requestorNotePreview,
  requestedByFromRow,
  reviewNotesFromRow,
  reviewNotesPreview,
  rrnFromRow,
  statusFromRow,
  truncateMiddle,
} from '../../utils/creditIssueRequestRows';

const PAGE_SIZE = 20;
const PER_WALLET_FETCH_LIMIT = 100;

function minorToInrString(minor) {
  if (minor == null) return '';
  const n = Number(minor);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toFixed(2);
}

function epochMsToLocalDatetime(ms) {
  if (!ms || !Number.isFinite(ms)) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** @param {URLSearchParams} params */
function walletIdFromSearchParams(params) {
  const raw = params.get('wallet_id') ?? params.get('walletId');
  const t = typeof raw === 'string' ? raw.trim() : '';
  return t !== '' ? t : '';
}

function GlobalCreditIssueRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const walletIdFilter = useMemo(() => walletIdFromSearchParams(searchParams), [searchParams]);
  const singleWalletMode = walletIdFilter !== '';

  const [loggingOut, setLoggingOut] = useState(false);
  const [pingStatus, setPingStatus] = useState(() => (isInternalWalletApiConfigured() ? 'loading' : 'skipped'));
  const [pingPayload, setPingPayload] = useState(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  /** @type {{ row: unknown, wallet_id: string }[]} */
  const [mergedRows, setMergedRows] = useState([]);
  /** Server total when `singleWalletMode` (public API pagination). */
  const [apiTotal, setApiTotal] = useState(0);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [activeWalletId, setActiveWalletId] = useState('');
  const [activeRequestId, setActiveRequestId] = useState('');
  const [approveAmountInr, setApproveAmountInr] = useState('');
  const [approveRrn, setApproveRrn] = useState('');
  const [approveRepaymentLocal, setApproveRepaymentLocal] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  /** From row metadata for approve dialog preview only. */
  const [activeRequestorNote, setActiveRequestorNote] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const runPing = useCallback(async () => {
    if (!isInternalWalletApiConfigured()) {
      setPingStatus('skipped');
      setPingPayload(null);
      return;
    }
    setPingStatus('loading');
    setPingPayload(null);
    try {
      const data = await getInternalWalletB2BPing();
      setPingPayload(data && typeof data === 'object' ? data : {});
      setPingStatus('ready');
    } catch (e) {
      console.error(e);
      setPingStatus('unreachable');
      setPingPayload(null);
    }
  }, []);

  useEffect(() => {
    void runPing();
  }, [runPing]);

  const features = pingPayload?.features;
  const reachable = pingPayload?.accessible !== false;
  const listAllowed =
    pingStatus === 'ready' && reachable && features?.wallet_credit_issue_requests === true;

  const loadRequestsGlobal = useCallback(async () => {
    if (!listAllowed) return;
    setListLoading(true);
    setListError(null);
    setApiTotal(0);
    try {
      const data = await listWallets();
      const all = Array.isArray(data?.wallets) ? data.wallets : [];
      const cashWallets = all.filter(
        (w) => w && String(w.currency ?? '').trim().toUpperCase() === 'CASH' && w.wallet_id != null && String(w.wallet_id).trim() !== '',
      );
      const paramsBase = {
        page: 1,
        limit: PER_WALLET_FETCH_LIMIT,
        ...(statusFilter && ['PENDING', 'APPROVED', 'REJECTED'].includes(statusFilter) ? { status: statusFilter } : {}),
      };
      /** @type {{ row: unknown, wallet_id: string }[]} */
      const merged = [];
      await Promise.all(
        cashWallets.map(async (w) => {
          const wid = String(w.wallet_id).trim();
          try {
            const res = await fetchPublicCreditIssueRequests(wid, paramsBase);
            if (res && typeof res === 'object' && res.success === false) return;
            const requests = Array.isArray(res?.requests) ? res.requests : [];
            requests.forEach((row) => merged.push({ row, wallet_id: wid }));
          } catch (e) {
            console.error(e);
          }
        }),
      );
      merged.sort((a, b) => createdMsFromRow(b.row) - createdMsFromRow(a.row));
      setMergedRows(merged);
    } catch (e) {
      console.error(e);
      setMergedRows([]);
      setListError(getAxiosErrorMessage(e, 'Failed to load requests'));
    } finally {
      setListLoading(false);
    }
  }, [listAllowed, statusFilter]);

  const loadRequestsSingleWallet = useCallback(async () => {
    if (!listAllowed || !walletIdFilter) return;
    setListLoading(true);
    setListError(null);
    try {
      const params = {
        page: listPage,
        limit: PAGE_SIZE,
        ...(statusFilter && ['PENDING', 'APPROVED', 'REJECTED'].includes(statusFilter) ? { status: statusFilter } : {}),
      };
      const res = await fetchPublicCreditIssueRequests(walletIdFilter, params);
      if (res && typeof res === 'object' && res.success === false) {
        setMergedRows([]);
        setApiTotal(0);
        setListError(typeof res.message === 'string' ? res.message : 'Failed to load requests');
        return;
      }
      const requests = Array.isArray(res?.requests) ? res.requests : [];
      setMergedRows(requests.map((row) => ({ row, wallet_id: walletIdFilter })));
      setApiTotal(Number(res?.total) >= 0 ? Number(res.total) : requests.length);
    } catch (e) {
      console.error(e);
      setMergedRows([]);
      setApiTotal(0);
      setListError(getAxiosErrorMessage(e, 'Failed to load requests'));
    } finally {
      setListLoading(false);
    }
  }, [listAllowed, walletIdFilter, listPage, statusFilter]);

  useEffect(() => {
    setListPage(1);
  }, [walletIdFilter]);

  useEffect(() => {
    if (!listAllowed) return;
    if (singleWalletMode) void loadRequestsSingleWallet();
    else void loadRequestsGlobal();
  }, [listAllowed, singleWalletMode, loadRequestsGlobal, loadRequestsSingleWallet]);

  const totalForPager = singleWalletMode ? apiTotal : mergedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalForPager / PAGE_SIZE));

  const pageSlice = useMemo(() => {
    if (singleWalletMode) return mergedRows;
    const start = (listPage - 1) * PAGE_SIZE;
    return mergedRows.slice(start, start + PAGE_SIZE);
  }, [singleWalletMode, mergedRows, listPage]);

  const clearWalletFilter = () => {
    setSearchParams({}, { replace: true });
    setListPage(1);
  };

  const reload = useCallback(() => {
    if (singleWalletMode) void loadRequestsSingleWallet();
    else void loadRequestsGlobal();
  }, [singleWalletMode, loadRequestsSingleWallet, loadRequestsGlobal]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutRemote();
    } finally {
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  const openApprove = (walletId, requestId, defaultRrn, requestorNote, amountMinor, repaymentMs) => {
    setActiveWalletId(walletId);
    setActiveRequestId(requestId);
    setApproveAmountInr(minorToInrString(amountMinor));
    setApproveRrn(typeof defaultRrn === 'string' && defaultRrn !== '—' ? defaultRrn : '');
    setApproveRepaymentLocal(epochMsToLocalDatetime(repaymentMs));
    setApproveNotes('');
    setActiveRequestorNote(typeof requestorNote === 'string' ? requestorNote : '');
    setApproveOpen(true);
  };

  const openReject = (walletId, requestId) => {
    setActiveWalletId(walletId);
    setActiveRequestId(requestId);
    setRejectNotes('');
    setRejectOpen(true);
  };

  const closeApprove = () => {
    if (!actionSubmitting) setApproveOpen(false);
  };

  const closeReject = () => {
    if (!actionSubmitting) setRejectOpen(false);
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    const wid = activeWalletId.trim();
    const rid = activeRequestId.trim();
    if (!wid || !rid) return;

    let amountOverride;
    if (approveAmountInr.trim()) {
      const parsed = parseInrMajorToMinor(approveAmountInr, { allowZero: false });
      if (!parsed.ok) {
        toast.error(parsed.message);
        return;
      }
      amountOverride = parsed.value;
    }

    let repayment_date;
    if (approveRepaymentLocal.trim()) {
      const ms = new Date(approveRepaymentLocal).getTime();
      if (!Number.isFinite(ms)) {
        toast.error('Repayment date is invalid.');
        return;
      }
      repayment_date = ms;
    }

    const rrnTrim = approveRrn.trim();
    const notesTrim = approveNotes.trim();
    setActionSubmitting(true);
    try {
      const issue =
        amountOverride !== undefined ? { amount_minor: amountOverride } : undefined;
      const metadata = {};
      if (notesTrim) metadata.notes = notesTrim;
      if (repayment_date !== undefined) metadata.repayment_date_ms = String(repayment_date);

      const body = {
        ...(rrnTrim ? { rrn: rrnTrim } : {}),
        ...(issue !== undefined ? { issue } : {}),
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      };
      const res = await postInternalApproveCreditIssueRequest(wid, rid, body);
      if (res && typeof res === 'object' && res.success === true) {
        toast.success('Request approved.');
        closeApprove();
        void reload();
      } else {
        toast.error(
          typeof res?.message === 'string' ? res.message : typeof res?.error === 'string' ? res.error : 'Approve failed.',
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(getInternalErrorMessage(err, 'Approve failed'));
    } finally {
      setActionSubmitting(false);
    }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    const wid = activeWalletId.trim();
    const rid = activeRequestId.trim();
    if (!wid || !rid) return;
    const notesTrim = rejectNotes.trim();
    setActionSubmitting(true);
    try {
      const body = notesTrim ? { metadata: { notes: notesTrim } } : {};
      const res = await postInternalRejectCreditIssueRequest(wid, rid, body);
      if (res && typeof res === 'object' && res.success === true) {
        toast.success('Request rejected.');
        closeReject();
        void reload();
      } else {
        toast.error(
          typeof res?.message === 'string' ? res.message : typeof res?.error === 'string' ? res.error : 'Reject failed.',
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(getInternalErrorMessage(err, 'Reject failed'));
    } finally {
      setActionSubmitting(false);
    }
  };

  const showAccessDenied =
    pingStatus === 'skipped' || (pingStatus === 'ready' && !listAllowed) || pingStatus === 'unreachable';

  return (
    <div>
      <header className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">{getWelcomeGreeting()}</p>
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
          >
            <i className={`material-icons-outlined text-base ${loggingOut ? 'animate-spin' : ''}`}>
              {loggingOut ? 'refresh' : 'logout'}
            </i>
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
        <Link to="/wallets" className="link-back mb-2">
          <i className="material-icons-outlined text-base">arrow_back</i>
          Your wallets
        </Link>
        <div className="flex gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-sky-700 text-white shadow-lg shadow-sky-500/25">
            <i className="material-icons-outlined text-3xl">fact_check</i>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-800">Internal API</p>
            <h1 className="page-title">Credit requests</h1>
            <p className="page-subtitle">
              {singleWalletMode ? (
                <>
                  Repayment requests for wallet{' '}
                  <span className="font-mono text-xs text-slate-800 break-all">{walletIdFilter}</span>. Approve or reject
                  using the internal API.
                </>
              ) : (
                <>
                  Pending and past repayment requests across all cash-credit wallets you can access. Approve or reject
                  using the internal API.
                </>
              )}
            </p>
            {singleWalletMode ? (
              <button
                type="button"
                onClick={clearWalletFilter}
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline"
              >
                <i className="material-icons-outlined text-base">filter_alt_off</i>
                Show all wallets
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {pingStatus === 'loading' ? (
        <div className="card-surface p-8">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        </div>
      ) : null}

      {showAccessDenied && pingStatus !== 'loading' ? (
        <div className="card-surface p-6">
          <div className="flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-950">
            <i className="material-icons-outlined shrink-0">vpn_lock</i>
            <div>
              <p className="font-medium">Credit requests are not available</p>
              <p className="mt-1 text-sm opacity-90">
                {pingStatus === 'skipped'
                  ? 'The internal wallet API URL is not configured for this build.'
                  : pingStatus === 'unreachable'
                    ? 'Could not reach the internal API. Sign in to it (Cloudflare Access) in this browser, or check the VPN, then retry.'
                    : 'Connect to VPN and ensure your account is allowed to review credit issue requests.'}
              </p>
              {pingStatus === 'unreachable' ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {INTERNAL_SIGN_IN_URL ? (
                    <button type="button" className="btn-primary text-sm" onClick={openInternalSignIn}>
                      Sign in to the internal API
                    </button>
                  ) : null}
                  <button type="button" className="btn-secondary text-sm" onClick={() => runPing()}>
                    Retry
                  </button>
                </div>
              ) : null}
              <Link to="/wallets" className="mt-3 inline-flex text-sm font-medium text-primary-700 hover:underline">
                Back to your wallets
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {listAllowed ? (
        <>
          <div className="card-surface mb-4 overflow-hidden p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                Status
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setListPage(1);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900"
                >
                  <option value="">All</option>
                  <option value="PENDING">PENDING</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                </select>
              </label>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-sm"
                disabled={listLoading}
                onClick={() => void reload()}
              >
                <i className={`material-icons-outlined text-base ${listLoading ? 'animate-spin' : ''}`}>refresh</i>
                Refresh
              </button>
            </div>

            {listError && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <i className="material-icons-outlined shrink-0 text-base">error_outline</i>
                <span>{listError}</span>
              </div>
            )}

            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-100">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead>
                  <tr className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {!singleWalletMode ? (
                      <th className="whitespace-nowrap px-3 py-3">Wallet</th>
                    ) : null}
                    <th className="whitespace-nowrap px-3 py-3">Request ID</th>
                    <th className="whitespace-nowrap px-3 py-3 text-right">Amount</th>
                    <th className="whitespace-nowrap px-3 py-3">RRN</th>
                    <th className="whitespace-nowrap px-3 py-3">Status</th>
                    <th className="whitespace-nowrap px-3 py-3">Requested by</th>
                    <th className="whitespace-nowrap px-3 py-3">Repayment date</th>
                    <th className="min-w-[8rem] px-3 py-3">Requestor note</th>
                    <th className="min-w-[8rem] px-3 py-3">Reviewer notes</th>
                    <th className="whitespace-nowrap px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {listLoading && (
                    <tr>
                      <td colSpan={singleWalletMode ? 9 : 10} className="px-3 py-10 text-center text-slate-500">
                        <i className="material-icons-outlined mr-1 inline-block animate-spin align-middle text-base">
                          refresh
                        </i>{' '}
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!listLoading && pageSlice.length === 0 && !listError && (
                    <tr>
                      <td colSpan={singleWalletMode ? 9 : 10} className="px-3 py-10 text-center text-slate-500">
                        {singleWalletMode
                          ? 'No requests for this wallet with the current filter.'
                          : 'No requests found for your cash-credit wallets.'}
                      </td>
                    </tr>
                  )}
                  {!listLoading &&
                    pageSlice.map((item, index) => {
                      const row = item.row;
                      const wId = item.wallet_id;
                      const rid = requestIdFromRow(row);
                      const st = statusFromRow(row);
                      const rrn = rrnFromRow(row);
                      const minor = amountMinorFromRow(row);
                      const badgeClass =
                        st === 'APPROVED'
                          ? 'bg-emerald-100 text-emerald-900'
                          : st === 'REJECTED'
                            ? 'bg-red-100 text-red-900'
                            : st === 'PENDING'
                              ? 'bg-amber-100 text-amber-900'
                              : 'bg-slate-100 text-slate-800';
                      const pending = st === 'PENDING';
                      return (
                        <tr key={`${wId}:${rid || index}`} className="hover:bg-slate-50/80">
                          {!singleWalletMode ? (
                            <td className="max-w-[11rem] px-3 py-2.5 text-xs">
                              <Link
                                to={`/wallets/${encodeURIComponent(wId)}/statement`}
                                className="font-mono font-medium text-primary-700 hover:text-primary-900 hover:underline"
                                title={`Open wallet statement (${wId})`}
                              >
                                {truncateMiddle(wId, 10, 6)}
                              </Link>
                            </td>
                          ) : null}
                          <td className="max-w-[12rem] px-3 py-2.5 font-mono text-xs text-slate-800" title={rid || ''}>
                            {rid ? truncateMiddle(rid) : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">
                            {minor != null ? formatWalletBalanceMinor(minor, 'INR') : '—'}
                          </td>
                          <td className="max-w-[10rem] break-all px-3 py-2.5 font-mono text-xs text-slate-700">{rrn}</td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
                              {st || '—'}
                            </span>
                          </td>
                          <td className="max-w-[10rem] break-all px-3 py-2.5 font-mono text-xs text-slate-700">
                            {requestedByFromRow(row)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{repaymentDateFromRow(row)}</td>
                          <td className="max-w-xs break-words px-3 py-2.5 text-xs text-slate-700" title={requestorNoteFromRow(row) || undefined}>
                            {requestorNotePreview(row)}
                          </td>
                          <td className="max-w-xs break-words px-3 py-2.5 text-xs text-slate-700" title={reviewNotesFromRow(row) || undefined}>
                            {reviewNotesPreview(row)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right">
                            {pending && rid ? (
                              <div className="flex flex-wrap justify-end gap-1">
                                <button
                                  type="button"
                                  className="btn-primary py-1 px-2 text-xs"
                                  onClick={() => openApprove(wId, rid, rrn, requestorNoteFromRow(row), minor, repaymentDateMsFromRow(row))}
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary border-red-200 bg-red-50 py-1 px-2 text-xs text-red-900 hover:bg-red-100"
                                  onClick={() => openReject(wId, rid)}
                                >
                                  Reject
                                </button>
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
              <span>
                {totalForPager > 0 ? (
                  <>
                    Page {listPage} of {totalPages} · {totalForPager} request{totalForPager === 1 ? '' : 's'}
                    {singleWalletMode ? ' (server)' : ' (loaded)'}
                  </>
                ) : (
                  <>—</>
                )}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary py-1 pl-2 pr-2.5 text-xs"
                  disabled={listLoading || listPage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn-secondary py-1 pl-2 pr-2.5 text-xs"
                  disabled={listLoading || listPage >= totalPages}
                  onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {approveOpen ? (
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 p-4"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) closeApprove();
              }}
            >
              <div
                className="card-surface max-h-[90vh] w-full max-w-md overflow-y-auto p-6 shadow-xl"
                role="dialog"
                aria-labelledby="approve-global-cir-title"
                onMouseDown={(ev) => ev.stopPropagation()}
              >
                <h2 id="approve-global-cir-title" className="text-lg font-semibold text-slate-900">
                  Approve request
                </h2>
                <p className="mt-1 font-mono text-xs text-slate-600 break-all">Wallet {activeWalletId}</p>
                <p className="mt-1 text-sm text-slate-600">
                  Request <span className="font-mono text-xs">{truncateMiddle(activeRequestId, 12, 8)}</span>
                </p>
                {activeRequestorNote.trim() ? (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Requestor note</p>
                    <p className="mt-1 whitespace-pre-wrap break-words">{activeRequestorNote}</p>
                  </div>
                ) : null}
                <form className="mt-4 flex flex-col gap-3" onSubmit={submitApprove}>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-slate-600">Amount (INR) — override</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={approveAmountInr}
                      onChange={(e) => setApproveAmountInr(e.target.value)}
                      placeholder="e.g. 10000.50"
                      className="rounded-lg border border-slate-200 px-3 py-2 font-mono text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-slate-600">RRN — override</span>
                    <input
                      type="text"
                      value={approveRrn}
                      onChange={(e) => setApproveRrn(e.target.value)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-slate-600">Repayment date and time (optional)</span>
                    <input
                      type="datetime-local"
                      value={approveRepaymentLocal}
                      onChange={(e) => setApproveRepaymentLocal(e.target.value)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-slate-600">Notes (optional)</span>
                    <textarea
                      value={approveNotes}
                      onChange={(e) => setApproveNotes(e.target.value)}
                      rows={2}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <div className="mt-2 flex flex-wrap justify-end gap-2">
                    <button type="button" className="btn-secondary" disabled={actionSubmitting} onClick={closeApprove}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={actionSubmitting}>
                      {actionSubmitting ? 'Submitting…' : 'Confirm approve'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}

          {rejectOpen ? (
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 p-4"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) closeReject();
              }}
            >
              <div
                className="card-surface max-h-[90vh] w-full max-w-md overflow-y-auto p-6 shadow-xl"
                role="dialog"
                aria-labelledby="reject-global-cir-title"
                onMouseDown={(ev) => ev.stopPropagation()}
              >
                <h2 id="reject-global-cir-title" className="text-lg font-semibold text-slate-900">
                  Reject request
                </h2>
                <p className="mt-1 font-mono text-xs text-slate-600 break-all">Wallet {activeWalletId}</p>
                <p className="mt-1 text-sm text-slate-600">
                  Request <span className="font-mono text-xs">{truncateMiddle(activeRequestId, 12, 8)}</span>
                </p>
                <form className="mt-4 flex flex-col gap-3" onSubmit={submitReject}>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-slate-600">Notes (optional)</span>
                    <textarea
                      value={rejectNotes}
                      onChange={(e) => setRejectNotes(e.target.value)}
                      rows={3}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <div className="mt-2 flex flex-wrap justify-end gap-2">
                    <button type="button" className="btn-secondary" disabled={actionSubmitting} onClick={closeReject}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-danger" disabled={actionSubmitting}>
                      {actionSubmitting ? 'Submitting…' : 'Confirm reject'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default GlobalCreditIssueRequestsPage;
